import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Reveal } from "@/hooks/useReveal";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/posts", { params: { limit: 200 } });
        setPosts(data || []);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <main data-testid="blog-page" className="bg-black text-white min-h-screen">
      <Navigation alwaysDark />

      {/* Header */}
      <section className="pt-40 md:pt-48 pb-20 md:pb-24 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <p className="text-[10px] tracking-[0.32em] uppercase mb-6 text-[#B8945A]">
              Insights &amp; Field Notes
            </p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl">
              From the
              <br />
              <em className="italic font-light">Coach&apos;s Desk.</em>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-base md:text-lg text-white/60 mt-8 max-w-2xl leading-relaxed">
              Field-tested notes on owning, improving, and selling a home in
              California — written by Alfredo Manzano.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Grid */}
      <section className="px-6 md:px-12 pb-32 md:pb-40">
        <div className="max-w-[1400px] mx-auto">
          {loading ? (
            <p className="text-sm text-white/40">Loading…</p>
          ) : posts.length === 0 ? (
            <div className="border border-dashed border-white/15 px-8 py-20 text-center">
              <p className="text-white/50">No posts published yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 100}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group block relative aspect-[4/5] overflow-hidden bg-[#0a0a0a]"
                  >
                    {p.hero_image_url ? (
                      <img
                        src={resolveImage(p.hero_image_url)}
                        alt=""
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1100ms] group-hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-black" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/0" />

                    <div className="absolute inset-x-0 bottom-0 p-7 md:p-8 z-10">
                      <p className="text-[10px] tracking-[0.32em] uppercase text-[#B8945A] mb-4">
                        {p.category || "Article"}
                      </p>
                      <h3 className="font-heading text-2xl md:text-[1.7rem] leading-[1.12] tracking-tight text-white">
                        {p.title}
                      </h3>
                      <p className="text-sm text-white/70 mt-4 leading-relaxed line-clamp-2">
                        {p.excerpt}
                      </p>
                      <div className="mt-5 flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-[0.24em] text-white/40">
                          {fmtDate(p.publish_date)}
                        </span>
                        <span className="text-[10px] tracking-[0.28em] uppercase text-[#B8945A] group-hover:text-white transition-colors">
                          Read &rarr;
                        </span>
                      </div>
                    </div>
                    <span className="absolute left-7 md:left-8 right-7 md:right-8 bottom-6 h-px bg-[#B8945A] w-0 group-hover:w-[calc(100%-3.5rem)] md:group-hover:w-[calc(100%-4rem)] transition-all duration-700" />
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Blog;
